import useReveal from '../hooks/useReveal';
import SectionHead from './SectionHead';

/**
 * Proof — what a small studio can honestly claim.
 *
 * The previous version aggregated "3.2× conversion lift" and "98 Lighthouse
 * average" across a fictional 18-month portfolio, plus four fabricated client
 * mocks (Meridian Dental, Harbor Motors, Northfield Law, Chef Zeitoun with
 * "+312% bookings"). For a studio whose entire positioning is honesty, that
 * content is disqualifying.
 *
 * Replacement: commitments we actually hold ourselves to on every build, each
 * one independently verifiable on the sites we ship. The numbers here are
 * performance budgets — targets we hit, not averages we manufactured.
 */
interface Commitment {
  k: string;
  title: string;
  body: string;
}

const COMMITMENTS: Commitment[] = [
  {
    k: '< 1.5s',
    title: 'Loads before patience runs out',
    body: 'On a median phone connection — not our office wifi. If a page on your site takes longer than that to show up, it doesn\'t ship. The visitor who almost left for a competitor stays.',
  },
  {
    k: '95+',
    title: "Passes Google's quality checks",
    body: 'Every page, every deploy, with room to spare. Performance, accessibility, best practices, SEO — Google grades all four. We test it ourselves before we hand the keys over, and you can run the same test on either project below.',
  },
  {
    k: '0',
    title: "Trackers your visitors didn't ask for",
    body: 'No Google Analytics, no Facebook pixels, no third-party fingerprinting bolted on without your say-so. The pages your customers load are the pages you approved — nothing else watching from the sidelines.',
  },
];

export default function Proof() {
  const bigRef = useReveal<HTMLUListElement>();

  return (
    <section id="proof" className="section proof">
      <div className="wrap">
        <SectionHead
          eyebrow="What we commit to"
          title={
            <>
              Standards, <em className="serif">not averages.</em>
            </>
          }
          body="We're a small studio. We won't quote you industry-wide stats we didn't measure. What we will show you is the bar every WebNova site has to clear before it goes live — three commitments you can verify yourself on the two projects above."
        />

        <ul ref={bigRef} className="proof-big reveal">
          {COMMITMENTS.map((c) => (
            <li key={c.title} className="cell">
              <div className="num" aria-hidden="true">
                {c.k}
              </div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
